/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps, Fragment, Ref } from 'preact';
import { forwardRef, useCallback, useRef } from 'preact/compat';
import { useFormContext } from '../hooks/UNSAFE_useFormContext';
import { FormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { FocusableHandle, useFocusableTextField } from '../hooks/UNSAFE_useFocusableTextField';
import { Label } from '../UNSAFE_Label';
import {
  ReadonlyTextField,
  ReadonlyTextFieldInput,
  TextField,
  TextFieldInput
} from '../UNSAFE_TextField';
import { useTextField } from '../hooks/UNSAFE_useTextField';
import {
  CompactUserAssistance,
  InlineUserAssistance,
  UserAssistanceDensityType
} from '../UNSAFE_UserAssistance';
import { CountUnit, useLengthFilter } from '../hooks/UNSAFE_useLengthFilter';
import { MaxLengthLiveRegion } from '../UNSAFE_TextField';
import { MaxLengthCounter } from './MaxLengthCounter';
import { Size } from '../utils/UNSAFE_size';
import { useCurrentValueReducer } from '../hooks/UNSAFE_useCurrentValueReducer';
import { ValueUpdateDetail } from '../utils/UNSAFE_valueUpdateDetail';

type TextFieldProps = ComponentProps<typeof TextField>;
type TextFieldInputProps = ComponentProps<typeof TextFieldInput>;
type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;

type Props = {
  /**
   * The ID of an element (or space separated IDs of multiple elements) that
   * describes the input.
   */
  'aria-describedby'?: TextFieldInputProps['aria-describedby'];

  /**
   * Text to provide guidance to help the user understand what to enter.
   */
  assistiveText?: InlineUserAssistanceProps['assistiveText'];
  /**
   * Dictates component's autocomplete state.
   * This attribute indicates whether the value of the control can be automatically completed by the browser.
   * The common values are "on" and "off".
   */
  autoComplete?: TextFieldInputProps['autoComplete'];
  /**
   * Autofocus specifies whether the component will get focus when the page is loaded.
   * If it is set to true then the associated component will get input focus when the page is loaded.
   * Setting this property doesn't set the focus to the component; it tells the browser to focus
   * to it when the element is inserted in the document.
   */
  autoFocus?: TextFieldInputProps['autoFocus'];
  /**
   * Help source URL associated with the component.
   */
  helpSourceLink?: InlineUserAssistanceProps['helpSourceLink'];
  /**
   * Custom text to be rendered for the <code>helpSourceLink</code>.
   */
  helpSourceText?: InlineUserAssistanceProps['helpSourceText'];
  /**
   * Specifies whether the component is disabled.
   */
  isDisabled?: boolean;
  /**
   * Specifies whether the component is readonly.
   */
  isReadonly?: boolean;
  /**
   * Specifies for accessibility purposes whether a value is required.
   *
   * Setting this property to <code>false</code> means that a value is not required to be
   * committed by the user.  Setting this property to <code>true</code> means that a value
   * is required to be committed by the user.
   */
  isRequired?: TextFieldInputProps['isRequired'];
  /**
   * Specifies whether to show an indicator on screen that a value is required, for example
   * before the user has committed a value.
   */
  isRequiredShown?: InlineUserAssistanceProps['isRequiredShown'];
  /**
   * Specifies the label associated with the field.
   */
  label: string;
  /**
   * Specifies where the label is positioned relative to the field.
   */
  labelEdge?: 'inside' | 'start' | 'top' | 'none';
  /**
   * Specifies the width of the label when <code>labelEdge</code> is <code>"start"</code>
   */
  labelStartWidth?: Size;
  /**
   * Specifies the maximum number of characters that can be entered in the input field.
   */
  maxLength?: number;
  /**
   * Determines whether the remaining number of characters that can be entered is shown or not.
   */
  maxLengthCounter?: 'none' | 'remaining';
  /**
   * Specifies how the input text characters are to be counted when maxLength is specified.
   */
  maxLengthUnit?: CountUnit;
  /**
   * Messages to show on screen that are associated with the component.
   */
  messages?: InlineUserAssistanceProps['messages'];
  /**
   * A short hint to display before the user enters a value.
   */
  placeholder?: TextFieldInputProps['placeholder'];
  /**
   * Specifies how the user is allowed to manually resize the TextArea component.
   */
  resize?: TextFieldProps['resize'];
  /**
   * Specifies the ARIA role type.
   */
  role?: TextFieldInputProps['role'];
  /**
   * Specifies the number of rows to display
   */
  rows?: TextFieldInputProps['rows'];
  /**
   * Specifies how to align text within the field.
   */
  textAlign?: TextFieldInputProps['textAlign'];
  /**
   * Specifies the density of the user assistance presentation. It can be set to:
   * <ul>
   * <li><code>'efficient'</code>: Show inline and reserve space to prevent layout reflow when user
   * assistance text is displayed.</li>
   * <li><code>'reflow'</code>: Show inline. Layout will reflow when text is displayed.</li>
   * <li><code>'compact'</code>: Messages, help, hints, and required will not be shown inline; they will show in a mode that keeps the screen more compact, like a popup for the messages, and a required icon to indicate Required. </li>
   * </ul>
   */
  userAssistanceDensity?: UserAssistanceDensityType;
  /**
   * The current display value of the component.
   */
  value?: TextFieldInputProps['value'];
  /**
   * The style variant of the component.
   */
  variant?: TextFieldInputProps['variant'];
  /**
   * Callback invoked when the user commits the entered value by blurring out of the field.
   */
  onCommit?: TextFieldInputProps['onCommit'];
  /**
   * Callback invoked each time the user changes the value of the field. For example, if the user
   * types 'abcd', this callback will be called four times with the value as 'a', 'ab', 'abc' and 'abcd'
   * respectively.
   */
  onInput: TextFieldInputProps['onInput'];
};

export const TextArea = forwardRef(
  (
    {
      'aria-describedby': ariaDescribedBy,
      assistiveText,
      autoComplete = 'off',
      autoFocus = false,
      helpSourceLink,
      helpSourceText,
      isDisabled: propIsDisabled,
      isReadonly: propIsReadonly,
      isRequired = false,
      isRequiredShown,
      label,
      labelEdge: propLabelEdge,
      labelStartWidth: propLabelStartWidth,
      maxLength,
      maxLengthCounter = 'remaining',
      maxLengthUnit,
      messages,
      placeholder,
      resize,
      role,
      rows,
      textAlign: propTextAlign,
      userAssistanceDensity: propUserAssistanceDensity,
      value,
      variant = 'default',
      onInput,
      onCommit
    }: Props,
    ref?: Ref<FocusableHandle>
  ) => {
    const { currentCommitValue, dispatch } = useCurrentValueReducer({ value });
    const onInputAndDispatch = useCallback(
      (detail: ValueUpdateDetail<string>) => {
        // Should dispatch happen first? This will queue up a re-render, ordering should not cause issues (this is async)
        dispatch({ type: 'input', payload: detail.value });
        onInput?.(detail);
      },
      [onInput]
    );
    const onCommitAndDispatch = useCallback(
      (detail: ValueUpdateDetail<string>) => {
        // Should dispatch happen first? This will queue up a re-render, ordering should not cause issues (this is async)
        dispatch({ type: 'commit', payload: detail.value });
        onCommit?.(detail);
      },
      [onCommit]
    );
    const {
      isDisabled: isFormDisabled,
      isReadonly: isFormReadonly,
      labelEdge: formLabelEdge,
      labelStartWidth: formLabelStartWidth,
      textAlign: formTextAlign,
      userAssistanceDensity: formUserAssistanceDensity
    } = useFormContext();
    // default to FormContext values if component properties are not specified
    const isDisabled = propIsDisabled ?? isFormDisabled;
    const isReadonly = propIsReadonly ?? isFormReadonly;
    const labelEdge = propLabelEdge ?? formLabelEdge;
    const labelStartWidth = propLabelStartWidth ?? formLabelStartWidth;
    const textAlign = propTextAlign ?? formTextAlign;
    const userAssistanceDensity = propUserAssistanceDensity ?? formUserAssistanceDensity;

    const { enabledElementRef, readonlyElementRef, focusProps, isFocused } = useFocusableTextField<
      HTMLTextAreaElement,
      HTMLTextAreaElement
    >({ isDisabled, isReadonly, ref });

    const { formFieldContext, inputProps, labelProps, textFieldProps, userAssistanceProps } =
      useTextField({
        ariaDescribedBy,
        contentVariant: 'textarea',
        helpSourceLink,
        helpSourceText,
        isDisabled,
        isFocused,
        isReadonly,
        isRequiredShown,
        labelEdge,
        messages,
        styleVariant: variant,
        userAssistanceDensity,
        value
      });

    // Handle length filter for user-typed inputs and controlled inputs
    const { isMaxLengthExceeded, valueLength, onFilteredInput } = useLengthFilter({
      maxLength,
      maxLengthUnit,
      value,
      onInput: onInputAndDispatch,
      onCommit: onCommitAndDispatch
    });

    const labelComp = labelEdge !== 'none' ? <Label {...labelProps}>{label}</Label> : undefined;

    const fieldLabelProps = {
      label: labelEdge !== 'none' ? labelComp : undefined,
      labelEdge: labelEdge !== 'none' ? labelEdge : undefined,
      labelStartWidth: labelEdge !== 'none' ? labelStartWidth : undefined
    };

    const ariaLabel = labelEdge === 'none' ? label : undefined;
    const isInlineDensity =
      userAssistanceDensity === 'efficient' || userAssistanceDensity === 'reflow';

    const inlineUserAssistance = isInlineDensity ? (
      isDisabled || isReadonly ? (
        // save space for user assistance if density is 'efficient', even though we don't
        // render user assistance for disabled or readonly fields
        userAssistanceDensity !== 'efficient' ? undefined : (
          <InlineUserAssistance
            userAssistanceDensity={userAssistanceDensity}
            {...userAssistanceProps}
          />
        )
      ) : (
        <InlineUserAssistance
          assistiveText={assistiveText}
          fieldLabel={label}
          helpSourceLink={helpSourceLink}
          helpSourceText={helpSourceText}
          messages={messages}
          isRequiredShown={isRequiredShown}
          userAssistanceDensity={userAssistanceDensity}
          {...userAssistanceProps}
        />
      )
    ) : undefined;

    const anchorRef = useRef<HTMLDivElement>(null);

    const compactUserAssistance =
      userAssistanceDensity === 'compact' ? (
        <CompactUserAssistance
          anchorRef={anchorRef}
          messages={messages}
          assistiveText={assistiveText}
          {...userAssistanceProps}
        />
      ) : undefined;

    if (isReadonly) {
      // TODO: We need to conditionally render the readonly innerReadonlyField as a textarea or div
      //  also, the div will need to use white-space: pre-wrap so that the text will wrap like textarea
      //  see JET-50636
      return (
        <FormFieldContext.Provider value={formFieldContext}>
          <ReadonlyTextField
            role="presentation"
            compactUserAssistance={compactUserAssistance}
            inlineUserAssistance={inlineUserAssistance}
            ref={anchorRef}
            variant="textarea"
            {...fieldLabelProps}>
            <ReadonlyTextFieldInput
              aria-describedby={inputProps['aria-describedby']}
              aria-label={ariaLabel}
              aria-labelledby={labelProps.id}
              as="textarea"
              elementRef={readonlyElementRef}
              rows={rows}
              autoFocus={autoFocus}
              textAlign={textAlign}
              value={value}
              variant="textarea"
              hasEmptyLabel={label === '' && labelEdge === 'none'}
              hasInsideLabel={label !== undefined && labelEdge === 'inside'}
            />
          </ReadonlyTextField>
        </FormFieldContext.Provider>
      );
    }

    const onClickCounter = useCallback(() => {
      const elm = enabledElementRef.current;
      // for setSelectionRange(), len needs to be a number or null.
      const len = elm ? elm.value.length : null;
      // Since the counter element is after the text, clicking in this
      // should put the selection at the end of the text
      enabledElementRef.current?.setSelectionRange(len, len);
      // Clicking the counter should put the focus on the input field
      enabledElementRef.current?.focus();
    }, []);

    const mainContent = (
      <Fragment>
        <TextFieldInput
          as="textarea"
          aria-label={ariaLabel}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          currentCommitValue={currentCommitValue}
          hasEmptyLabel={label === '' && labelEdge === 'none'}
          hasInsideLabel={labelComp !== undefined && labelEdge === 'inside'}
          isRequired={isRequired}
          inputRef={enabledElementRef}
          onCommit={onCommitAndDispatch}
          onInput={onFilteredInput}
          placeholder={placeholder}
          role={role}
          rows={rows}
          textAlign={textAlign}
          value={value}
          {...inputProps}
        />
        {maxLengthCounter === 'remaining' && maxLength !== undefined && (
          <MaxLengthCounter
            {...{ maxLength, valueLength, parentHasFocus: isFocused, onClick: onClickCounter }}
          />
        )}
        {maxLength !== undefined && (
          <MaxLengthLiveRegion {...{ isMaxLengthExceeded, maxLength, valueLength }} />
        )}
      </Fragment>
    );

    return (
      <FormFieldContext.Provider value={formFieldContext}>
        <TextField
          mainContent={mainContent}
          compactUserAssistance={compactUserAssistance}
          inlineUserAssistance={inlineUserAssistance}
          onBlur={focusProps?.onfocusout}
          onFocus={focusProps?.onfocusin}
          mainFieldRef={anchorRef}
          resize={resize}
          {...textFieldProps}
          {...fieldLabelProps}></TextField>
      </FormFieldContext.Provider>
    );
  }
);
